<template>
<div v-if="userManagerStore.user" class="min-h-screen flex-1">
    <div ref="topbar" class="fixed top-0 w-screen z-30 transition-shadow duration-500 border-b border-transparent xl:border-black/[0.06] dark:xl:border-white/[0.05]"
      :class="[
        { 'shadow-sm': scrollYPx > 0 },
        { 'shadow-none': scrollYPx <= 0 },
        { 'xl:!border-transparent': hideTopBar }
      ]">
      <TopBarMain v-if="!hideTopBar"
        :title="title"
        :show-drawer="showDrawerNav"
        :center-local-time="centerLocalTime"
        :should-show-time="shouldShowTime"
        :current-center-id="currentCenterId"
        :is-desktop="isDesktop" />
      <SecondaryTabs v-if="showTabs" class="bg-background/50 backdrop-blur-xl pb-0"
        @transition="setTransitionFromEvent"
        :centers="userManagerStore.displayedCenters" :active-tab="activeTab" />
      <ProgressBar v-if="appStore.barSteps > 0"
        :current="appStore.pageLoadingProgress"
        class="absolute z-50 bottom-0 left-0 right-0" />
    </div>

    <!-- Desktop global sidebar -->
    <aside v-if="isDesktop" class="gds-wrapper">
      <GlobalDesktopSidebar />
    </aside>

    <div class="absolute top-0 flex flex-col flex-1 w-full h-full content"
      style="padding-top: var(--topbar-height)">
      <main ref="mainContent"
        class="relative grow flex flex-col flex-1"
        @scroll="updateScrollY">
        <!-- <div
          v-if="salesReportsStore.lastUpdated && salesReportsStore.salesReports && salesReportsStore.salesReports?.length > 0"
          class="w-full flex justify-end items-center text-xs text-gray-500 py-2 px-5">
          <div class="flex flex-col items-center">
            <div>
              {{ $t('updated_at', { date: formattedUpdatedAt }) }}
            </div>

          </div>
        </div> -->
        <router-view v-slot="{ Component }">
          <Transition :name="transition">
            <keep-alive :include="['DashboardLayout', 'Center']">
              <component :is="Component" :key="keyBasedOnRouteParams"
                @loaded="setTitle" @dashboard="setDashboardTitle" class="py-4 px-safe pb-safe md:px-8 md:py-5"
                :style="isDesktop ? { paddingLeft: 'calc(var(--sidebar-width) + var(--sidebar-gap))', paddingRight: '2rem' } : {}" />
            </keep-alive>
          </Transition>
        </router-view>
      </main>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useUserManagerStore } from '../stores/userManagerStore';
import { useProfileStore } from '../stores/profileStore';
import { useAppSettingsStore } from '../stores/AppSettingsStore';
import { RouteLocationNamedRaw } from 'vue-router';
import { Center } from '../user';
import { Direction } from '../GoManager';
import { useI18n } from 'vue-i18n';

import TopBarMain from '../components/TopBar/TopBarMain.vue';
import SecondaryTabs from '../components/tabs/SecondaryTabs.vue';
import GlobalDesktopSidebar from '../components/desktop/GlobalDesktopSidebar.vue';
import ProgressBar from '../components/ProgressBar.vue';
import Hammer from 'hammerjs';
import router from '../routes';
import { useCenterTime } from '../composables/useCenterTime';
import { useIsDesktop } from '../composables/useIsDesktop';

const userManagerStore = useUserManagerStore();
const profileStore = useProfileStore();
const appStore = useAppSettingsStore();
const { locale, t } = useI18n();

const currentCenterId = computed(() => {
  const id = router.currentRoute.value.params.centerId as string
  return id ? parseInt(id) : null
})

const { centerLocalTime, shouldShowTime } = useCenterTime(currentCenterId)
const isDesktop = useIsDesktop()

const hideTopBar = computed(() => isDesktop.value && userManagerStore.displayedCenters.length <= 1)

const mainContent = ref<HTMLElement | null>(null);
const transition = ref(isDesktop.value ? "page-fade" : "fade");
const mc = ref<HammerManager | null>(null);
const topbar = ref<HTMLElement | null>(null);

const title = ref(t('common.dashboard_title'));

const setTitle = (center: Center) => {
  title.value = center?.preferences?.label || center?.title || t('common.dashboard_title')
}

const setDashboardTitle = () => {
  title.value = t('common.dashboard_title');
}

/**
 * Show the scrollable segmented control for any multi-center user.
 * The component handles 2, 5, or 10 centers seamlessly via horizontal scroll.
 */
const showTabs = computed(() => {
  if (isDesktop.value) return false
  if (!userManagerStore.user) return false;

  return userManagerStore.displayedCenters.length > 1
})

/**
 * If there are centers and they are not show in tabs, show the drawer nav
 * (it means there is more than 5 centers)
 * 
 * If there is only one center, don't show the drawer nav
 */
const showDrawerNav = computed(() => {
  if (isDesktop.value) return false
  if (!userManagerStore.user) return false;
  if (showTabs.value) return false;

  return userManagerStore.displayedCenters.length > 1
})


const keyBasedOnRouteParams = computed(() => {
  const route = router.currentRoute.value;
  // Dashboard child routes share the same layout — use a consistent key
  if (route.path.startsWith('/dashboard')) {
    return 'Dashboard';
  }
  const key = route.name as string + (route.params.centerId ? route.params.centerId : '');
  return key;
})

const pages = computed(() => {
  const centers = userManagerStore.displayedCenters;

  let p: RouteLocationNamedRaw[] = [];
  if (centers.length > 1) {
    p.push({ name: 'Dashboard' });
  }

  // The first page is always the dashboard
  // The take the centers and map them to the center page
  centers?.forEach(center => {
    p.push({ name: 'Center', params: { centerId: center.id } })
  })

  return p;
})

const activeTab = computed(() => {
  const currentRoute = router.currentRoute.value;
  return pages.value.findIndex(page => page.name === currentRoute.name && (page.params?.centerId as string) == (currentRoute.params.centerId as string))
});

const updateScrollY = () => {
  const rect = mainContent.value?.getBoundingClientRect()
  if (!rect) return;

  currentMcY.value = rect.y;
}

const defaultMcY = ref(0)
const currentMcY = ref(0)

const scrollYPx = computed(() => {
  return defaultMcY.value - currentMcY.value;
})

onMounted(async () => {
  await profileStore.loadLanguage();
  locale.value = profileStore.language?.code || 'en'

  if (!mainContent.value) return;

  if (!isDesktop.value) {
    mc.value = new Hammer(mainContent.value);

    mc.value.on("swipeleft swiperight", (ev) => {
      if (!pages.value) return;

      let nextTab: number = 0;
      let nextPage: RouteLocationNamedRaw | undefined;
      const currentRoute = router.currentRoute.value;
      const currentPage = pages.value.findIndex(page => page.name === currentRoute.name && (page.params?.centerId as string) == (currentRoute.params.centerId as string));

      if (ev.type === "swipeleft") {
        transition.value = 'tab-slide-left';
        nextTab = currentPage + 1;
      } else if (ev.type === "swiperight") {
        transition.value = 'tab-slide-right';
        nextTab = currentPage - 1;
      }

      nextPage = pages.value[nextTab];

      if (nextPage) {
        router.push(nextPage);
      }
    });
  }

  const rect = mainContent.value.getBoundingClientRect();
  defaultMcY.value = rect.y;
  currentMcY.value = rect.y;
  window.addEventListener('scroll', updateScrollY);

  // Sync --topbar-height with actual topbar size (varies by page content)
  if (topbar.value) {
    const ro = new ResizeObserver(() => {
      const h = topbar.value?.getBoundingClientRect().height
      if (h) document.documentElement.style.setProperty('--topbar-height', `${Math.round(h)}px`)
    })
    ro.observe(topbar.value)
  }
})

// const salesReportsStore = useSalesReportsStore();
// const formattedUpdatedAt = computed(() => {
//   if (!salesReportsStore.lastUpdated) return '';

//   return new Intl.DateTimeFormat(navigator.language, {
//     hour: 'numeric',
//     minute: 'numeric'
//   }).format(salesReportsStore.lastUpdated);
// })

const setTransitionFromEvent = (direction: Direction) => {
  if (direction === 'left') {
    transition.value = 'tab-slide-left';
  } else {
    transition.value = 'tab-slide-right';
  }
}

watch(isDesktop, async () => {
  await nextTick()
  if (mainContent.value) {
    const rect = mainContent.value.getBoundingClientRect()
    defaultMcY.value = rect.y
    currentMcY.value = rect.y
  }
})

onUnmounted(() => {
  mc.value?.off("swipeleft swiperight");
  window.removeEventListener('scroll', updateScrollY);
})
</script>

<style>
.gds-wrapper {
  position: fixed;
  left: 0;
  top: var(--topbar-height);
  z-index: 20;
  height: calc(100vh - var(--topbar-height, 55px));
  width: var(--sidebar-width);
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
}

:is(.dark *).gds-wrapper {
  background: rgba(255, 255, 255, 0.03);
  border-right-color: rgba(255, 255, 255, 0.06);
}
</style>